<template>
  <div class="table-wrapper">
    <collection-details v-if="getterPopupName === 'CollectionDetails'"
                :collection="popupDetails" :webhooksAllowed="webhooksAllowed"
                @refreshCollection="refreshDetails"
                :manageAccess="manageAccess" />
    <collection-create v-else-if="getterPopupName === 'CollectionCreate'"
                @refreshList="updateFilters" />
    <collection-filter v-else-if="getterPopupName === 'CollectionFilter'"
                @updateFilters="updateFilters" />
    <collection-export v-else-if="getterPopupName === 'CollectionExport'"
                :exportOptions="paginate" :exportRecords="exportRecords" />
    <refunds-list v-else-if="getterPopupName === 'RefundsList'"
                :list="refundsList" />
    <refund-confirmation v-else-if="getterPopupName === 'RefundConfirmation'"
                @refreshList="refreshList"
                :details="popupDetails" />

    <div class="table-title">
      <div class="subject">
        <p>UPI Collections</p>

        <collection-webhooks v-if="list.length && !searchTerm && webhooksAllowed"
              :paginate="paginate" @resendWebhooks="bulkResendWebhooks" />
      </div>

      <div class="filters">
        <div class="options">
          <button @click="openPopup('CollectionCreate')" v-if="manageAccess.collection"
                  vt-data="create">
            <span>{{isDesktop() ? 'Create Collection' : 'Create'}}</span>
            <i class="fa fa-plus" />
          </button>
          <button @click="openPopup('CollectionExport')">
            Export
            <i class="fa fa-share" />
          </button>
          <button @click="openPopup('CollectionFilter')"
                  :class="{'filters-applied': filtersApplied}"
                  class="dark-btn">
            Filter
            <i class="fa fa-filter" />
          </button>
        </div>

        <collection-search placeholder="Search UPI ID / Order ID / Bank ref / UDFs"
                  @populateList="clearSearch" @setFunctions="setSearchFunctions"
                  :triggerSearch="(init) => triggerSearchPaginate(false, init)"
                  :searchTerm="searchTerm" />
      </div>
    </div>

    <div class="table-head" v-if="isDesktop()">
      <p class="d-xl">UPI ID</p>
      <p>Amount</p>
      <p class="d-l">Order ID</p>
      <p class="d-l">Status</p>
      <p>Created Time</p>
      <p class="d-l">Bank Reference Number</p>
      <p v-if="manageAccess.txns">Refunds</p>
    </div>

    <div class="table-body">
      <div class="table-row cursor"
        v-for="(collection, index) in list" :key="index"
        @click="showDetails(collection)"
      >
        <p class="d-xl">
          <span>{{collection.upi_id}}</span>
        </p>
        <p>
          <b v-if="!isDesktop()">Txn amt:</b>
          {{amountFormat(collection.amount)}}
        </p>
        <p class="d-l">
          <b v-if="!isDesktop()">Order ID:</b>
          <span>{{collection.order_id}}</span>
        </p>
        <p class="mt-grey-text d-l">
          {{collection.status}}
          <i v-if="['PENDING', 'REFUND_PENDING'].includes(collection.status)"
            class="fa fa-sync-alt"
            @click="refreshDetails($event, collection)"
          />
        </p>
        <p class="mt-grey-text">
          <b v-if="!isDesktop()">Created:</b>
          {{collection.created_at}}
        </p>
        <p class="d-l">
          <b v-if="!isDesktop()">Bank ref num:</b>
          <span>{{collection.bank_ref_num}}</span>
        </p>
        <p v-if="manageAccess.txns && ['REFUND_PENDING', 'REFUND_COMPLETED', 'REFUND_FAILED'].includes(collection.status)"
          class="download mt-col-span2"
          @click="viewRefunds($event, collection.id)"
        >
          Refunds
        </p>
        <p v-else-if="manageAccess.txns && isDesktop()">-</p>
      </div>
    </div>

    <table-loader v-if="!paginate.data_added || !paginate.stop" />

    <div class="table-empty" v-else-if="list.length === 0">
      <p v-if="searchTerm">Unable to find UPI collection with <span class="highlight">{{searchTerm}}</span>.<br>Please search with the complete UPI ID / Order ID / Bank ref number / UDFs.<br>Search is case sensitive - please search with the correct case to get the valid results.</p>
      <p v-else>{{emptyRecords}}</p>
    </div>
  </div>
</template>

<script>
import CollectionWebhooks from '@/components/global/BulkResendWebhooks'
import CollectionSearch from '@/components/global/SearchRecords'
import CollectionExport from '@/components/global/ExportConfirmation'
import CollectionDetails from '@/components/UPIs/Collections/Details'
import CollectionCreate from '@/components/UPIs/Collections/Create'
import CollectionFilter from '@/components/UPIs/Collections/Filter'
import RefundConfirmation from '@/components/UPIs/Collections/RefundConfirmation'
import RefundsList from '@/components/global/ExpandCollapseList'

import ApiServices from '@/services/api/upiCollections'
import listMixins from '@/mixins/listMixins'
import { amountFormat } from '@/utils/helpers'
import { mapMutations } from 'vuex'

export default {
  name: 'UPICollections',
  components: { CollectionDetails, CollectionFilter, CollectionCreate, CollectionSearch, CollectionExport, CollectionWebhooks, RefundsList, RefundConfirmation },
  mixins: [listMixins],
  data () {
    return {
      manageAccess: {
        collection: true
      },
      refundsList: []
    }
  },
  created () {
    this.startProcessing()
  },
  methods: {
    amountFormat,
    ...mapMutations(['SET_UPI_CONNECTED_BANKING']),
    populateParams () {
      this.api['search'] = ApiServices.search
      this.api['bulkResendWebhook'] = ApiServices.bulkResendWebhook
      this.api['export'] = ApiServices.export
      this.api['fetchStatus'] = ApiServices.fetchStatus
      this.keys['list'] = 'upi_collections'
      this.keys['popup'] = 'CollectionDetails'
      this.keys['defaultQuery'] = 'order_id'
      this.paginate['type'] = 'UPI collection'
      this.paginate['token_type'] = 'next_page_token'
      this.setHighlightSelectors([1, 3, 6])
    },
    fetchRecords (start) {
      return new Promise(resolve => {
        let params = {...this.filterOptions}
        if (start) {
          params['next_page_token'] = this.paginate['next_page_token']
        }

        ApiServices.list(params).then(response => {
          this.SET_STATEMENT_FILTER(this.filterOptions)
          if (response.reason === 'UNPRIVILEGED') {
            return
          }

          let data = response.data
          this.setAccess(response.privileges)
          if (start) {
            this.list.push(...data.upi_collections)
            this.checkPaginateV2(data.next_page_token)
          } else {
            this.list = data.upi_collections
            this.setPaginate(data)
            this.populateMeta(response)
          }

          resolve()
        })
      })
    },
    populateMeta ({data, privileges}, fromSearch) {
      this.webhooksAllowed = data.webhook_allowed
      this.SET_UPI_CONNECTED_BANKING(data.connected_banking_enabled)

      if (fromSearch) {
        this.setAccess(privileges)
      }
    },
    setAccess (privileges) {
      this.manageAccess = {
        collection: privileges.manage_upi_collections,
        txns: privileges.upi_collection_txns
      }
    },
    viewRefunds (event, id) {
      event.stopPropagation()
      ApiServices.refunds(id).then(response => {
        if (response.success) {
          this.refundsList = response.data.refunds.map(e => {
            e.popup = e.details
            return e
          })
          this.openPopup('RefundsList')
        } else {
          this.errorToastMsg(response.message)
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '@/assets/scss/table.scss';

  .desktop {
    .table-row {
      p {
        word-break: break-all;
      }
    }
  }

  .tablet, .mobile {
    .table-row {
      grid-template-areas: "upi_id created_time"
                            "amount order_id"
                            "status bank_ref_num";

      p {
        white-space: unset !important;

        &:nth-child(1) {
          grid-area: upi_id;
          color: $primary;
        }
        &:nth-child(2) {
          grid-area: amount;
        }
        &:nth-child(3) {
          grid-area: order_id;
          text-align: right;
        }
        &:nth-child(4) {
          grid-area: status;
        }
        &:nth-child(5) {
          grid-area: created_time;
          text-align: right;
        }
        &:nth-child(6) {
          grid-area: bank_ref_num;
          text-align: right;
        }
      }
    }
  }
</style>
